import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatbot_initialize, delete_session, vendors_by_business_impact_graph, get_bank_list, add_bank, invite_banks } from '../services';
import Modal from 'react-bootstrap/Modal';
import ButtonLoader from '../Components/ButtonLoader';
import NmtAI from '../NmtAI/Nmtai';
import VendorsByBusinessImpact from './ThirdpartyExternalExposure/VendorsByBusinessImpact';
import '../styles/dashboardV2.scss';
import '../styles/AddOns.scss';

// Lazy loaded components
const ThirdPartyExternalExposure = lazy(() => import('./ThirdpartyExternalExposure/ThirdPartyExternalExposure'));
const RiskRegistry = lazy(() => import('./RiskRegistry/RiskRegistry'));
const ComplianceReady = lazy(() => import('./ComplianceReady/ComplianceReady'));

const worstVendorsData = [
    { name: 'TechServ Solutions', banks: 8, findings: 14, percent: 22, barPercent: 30, barColor: '#EF4444', trend: 'Declining', trendColor: '#EF4444' },
    { name: 'CloudNet India Pvt', banks: 5, findings: 11, percent: 28, barPercent: 38, barColor: '#EF4444', trend: 'Declining', trendColor: '#EF4444' },
    { name: 'DataPro Systems', banks: 12, findings: 9, percent: 35, barPercent: 50, barColor: '#EF4444', trend: 'Improving', trendColor: '#10B981' },
    { name: 'SecureLink Infra', banks: 6, findings: 7, percent: 41, barPercent: 60, barColor: '#F97316', trend: 'Declining', trendColor: '#EF4444' },
    { name: 'NetBridge Corp', banks: 4, findings: 6, percent: 44, barPercent: 65, barColor: '#F97316', trend: 'Improving', trendColor: '#10B981' },
];

const auditFindings = [
    { date: '2026-02-22', severity: 'Critical', text: 'IAM privilege escalation path detected', bank: 'Punjab National Bank', clause: 'Clause 4.2.1', color: '#EF4444', bg: 'rgba(239,68,68,0.18)' },
    { date: '2026-02-21', severity: 'Critical', text: 'Encryption at rest not enabled on 3 databases', bank: 'Central Bank of India', clause: 'Clause 5.1.3', color: '#EF4444', bg: 'rgba(239,68,68,0.18)' },
    { date: '2026-02-20', severity: 'High', text: 'DR failover test not conducted in 180 days', bank: 'ICICI Bank', clause: 'Clause 6.3.2', color: '#F97316', bg: 'rgba(249,115,22,0.18)' },
    { date: '2026-02-19', severity: 'High', text: 'Third-party vendor access not reviewed quarterly', bank: 'Bank of India', clause: 'Clause 7.1.4', color: '#F97316', bg: 'rgba(249,115,22,0.18)' },
    { date: '2026-02-18', severity: 'High', text: 'SOC monitoring gaps during non-business hours', bank: 'Canara Bank', clause: 'Clause 3.4.1', color: '#F97316', bg: 'rgba(249,115,22,0.18)' },
    { date: '2026-02-17', severity: 'Medium', text: 'Cloud security group overly permissive rules', bank: 'State Bank of India', clause: 'Clause 5.2.2', color: '#FBBF24', bg: 'rgba(251,191,36,0.18)' },
    { date: '2026-02-15', severity: 'Medium', text: 'Password policy below RBI recommended strength', bank: 'Bank of Baroda', clause: 'Clause 4.1.5', color: '#FBBF24', bg: 'rgba(251,191,36,0.18)' },
];



const tabItems = [
    { title: 'FORESEE AI', key: 'nova' },
    // { title: 'Risk Registry', key: 'risk' },
    // { title: 'TPCRM', key: 'tpcrm' },
    // { title: 'GRC', key: 'grc' },
];

function DashboardV2() {
    const [aiInitialized, setAiInitialized] = useState(null);
    const [remainingCredits, setRemainingCredits] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [bankSearch, setBankSearch] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(true);
    const [vendorBusinessData, setVendorBusinessData] = useState('');
    const [activeTab, setActiveTab] = useState('nova');
    const [bankList, setBankList] = useState([]);
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [addBankForm, setAddBankForm] = useState({ bank_name: '', email: '' });
    const [addBankLoading, setAddBankLoading] = useState(false);
    const [addBankErrors, setAddBankErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    const fetchBankList = async () => {
        try {
            const response = await get_bank_list();
            const data = response?.statusCode?.response_data?.data || response?.statusCode?.response_data || response?.data || [];
            setBankList(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching bank list:', error);
        }
    };

    const handleAddBank = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!addBankForm.bank_name.trim()) errors.bank_name = 'Bank name is required';
        if (!addBankForm.email.trim()) errors.email = 'Email is required';
        setAddBankErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setAddBankLoading(true);
        try {
            const response = await add_bank({ bank_name: addBankForm.bank_name, email: addBankForm.email });
            const resData = response?.statusCode || response?.data || response;
            if (resData?.status === 1 || resData?.response_data?.status_code === 201) {
                const bankId = resData?.response_data?.data?.id;
                // Auto-invite the newly created bank
                if (bankId) {
                    try {
                        await invite_banks({ bank_ids: [bankId] });
                    } catch (inviteErr) {
                        console.error('Bank invite failed:', inviteErr);
                    }
                }
                showToast('Bank added successfully!', 'success');
                setShowAddBankModal(false);
                setAddBankForm({ bank_name: '', email: '' });
                fetchBankList();
            } else if (resData?.error) {
                showToast(resData.error.message || 'Failed to add bank', 'error');
            } else {
                showToast('Failed to add bank', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.error?.message || 'Failed to add bank', 'error');
        } finally {
            setAddBankLoading(false);
        }
    };

    const sectionsRef = {
        nova: useRef(null),
        risk: useRef(null),
        tpcrm: useRef(null),
        grc: useRef(null),
    };

    const filteredBanks = bankList.filter(b => {
        const name = b?.name || b?.bank_name || '';
        return name.toLowerCase().includes(bankSearch.toLowerCase());
    });
    const navigate = useNavigate();

    useEffect(() => {
        const initializeChatbot = async () => {
            try {
                await delete_session();
                const response = await chatbot_initialize();
                const data = response?.statusCode?.response_data?.data;
                const statusCode = response?.statusCode?.response_data?.status_code;
                if (statusCode === 200) {
                    setAiInitialized(data);
                    setRemainingCredits(data?.remaining_credits ?? 0);
                }
            } catch (error) {
                console.error('Error initializing chatbot:', error);
            } finally {
                setIsReady(true);
            }
        };
        initializeChatbot();
    }, []);

    // Fetch Vendors By Business Impact
    useEffect(() => {
        const fetchVendorsByBusinessImpact = async () => {
            try {
                const response = await vendors_by_business_impact_graph({});
                const heatMapData = response?.statusCode?.response_data?.data;
                setVendorBusinessData(heatMapData);
            } catch (error) {
                console.error('Error fetching vendor business impact:', error);
            }
        };
        fetchVendorsByBusinessImpact();
    }, []);

    // Fetch Bank List
    useEffect(() => {
        fetchBankList();
    }, []);

    // IntersectionObserver for active tab highlight on scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveTab(entry.target.id);
                    }
                });
            },
            { root: null, threshold: 0.4 }
        );

        Object.keys(sectionsRef).forEach((id) => {
            const el = sectionsRef[id]?.current;
            if (el) observer.observe(el);
        });

        return () => {
            Object.keys(sectionsRef).forEach((id) => {
                const el = sectionsRef[id]?.current;
                if (el) observer.unobserve(el);
            });
        };
    }, [isReady]);

    const handleScroll = (e, ref) => {
        e.preventDefault();
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <>
            <div className='cstm_dashboard_tabs'>
                {/* Tab Navigation */}
                <ul className="tab-navigation sticky-tab">
                    {tabItems.map((tab) => (
                        <li key={tab.key}>
                            <a
                                href={`#${tab.key}`}
                                onClick={(e) => handleScroll(e, sectionsRef[tab.key])}
                                className={activeTab === tab.key ? 'active' : ''}
                            >
                                {tab.title}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className="tab-contents">
                    {/* FORESEE AI Section */}
                    <div className='scroll_section' id="nova" ref={sectionsRef.nova}>
                        {isReady && (
                            <NmtAI
                                initializedData={aiInitialized}
                                remainingCredits={remainingCredits}
                                onCreditsUpdate={setRemainingCredits}
                            />
                        )}

                        {/* Static Dashboard Cards */}
                        <div className="dashboard_v2_cards">
                            <VendorsByBusinessImpact isWide={true} data={vendorBusinessData} />

                            <div>
                                <div className="v2_card_right_col">
                                    {/* Top 5 Worst Vendors */}
                                    <div className="v2_card v2_card_vendors">
                                        <div className="vendors_header">
                                            <span className="vendors_icon">⚠️</span>
                                            <h3 className="v2_card_title mb-0">Top 5 Worst Vendors</h3>
                                        </div>
                                        <div className="vendors_list">
                                            {worstVendorsData.map((item, idx) => (
                                                <div className="vendor_row" key={idx}>
                                                    <div className="vendor_rank_circle">{idx + 1}</div>
                                                    <div className="vendor_info">
                                                        <div className="vendor_name">{item.name}</div>
                                                        <div className="vendor_meta">
                                                            <span className="vendor_banks">{item.banks} banks</span>
                                                            <span className="vendor_findings">{item.findings} critical findings</span>
                                                        </div>
                                                        <div className="vendor_bar">
                                                            <div className="vendor_bar_fill" style={{ width: `${item.barPercent}%`, backgroundColor: item.barColor }}></div>
                                                        </div>
                                                    </div>
                                                    <div className="vendor_score">
                                                        <span className="vendor_percent" style={{ color: item.trendColor }}>{item.percent}%</span>
                                                        <span className="vendor_trend" style={{ color: item.trendColor }}>
                                                            {item.trend === 'Declining' ? '▼' : '▲'} {item.trend}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Recent Audit Findings */}
                                <div className="v2_card v2_card_audit mt-3">
                                    <div className="audit_header">
                                        <span className="audit_icon">📁</span>
                                        <h3 className="v2_card_title mb-0">Recent Audit Findings</h3>
                                    </div>
                                    <div className="audit_list">
                                        {auditFindings.map((item, idx) => (
                                            <div className="audit_row" key={idx}>
                                                <div className="audit_main">
                                                    <div className="audit_top">
                                                        <span className="audit_badge" style={{ color: item.color, backgroundColor: item.bg }}>{item.severity}</span>
                                                        <span className="audit_text">{item.text}</span>
                                                    </div>
                                                    <div className="audit_meta">
                                                        <span className="audit_bank">🏦 {item.bank}</span>
                                                        <span className="audit_clause">📂 {item.clause}</span>
                                                    </div>
                                                </div>
                                                <span className="audit_date">{item.date}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row mt-3'>


                            <div className='col-md-4'>
                                <div className="v2_card v2_card_bank">
                                    <div className="bank_header">
                                        <h3 className="v2_card_title mb-0">Bank Selector</h3>
                                        <span className="bank_badge">{bankList.length} Banks Monitored</span>
                                    </div>
                                    <div className="bank_dropdown_wrap">
                                        <div className="bank_select" onClick={() => setDropdownOpen(!dropdownOpen)}>
                                            <span>{selectedBank || 'Bank Selector'}</span>
                                            <span className="bank_arrow">▾</span>
                                        </div>
                                        {dropdownOpen && (
                                            <div className="bank_dropdown_list">
                                                <div className="bank_search_wrap">
                                                    <span className="bank_search_icon">🔍</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Search..."
                                                        value={bankSearch}
                                                        onChange={(e) => setBankSearch(e.target.value)}
                                                        className="bank_search_input"
                                                    />
                                                </div>
                                                <div className='card_max_height'>
                                                    {filteredBanks.map((bank, idx) => {
                                                        const bankName = bank?.name || bank?.bank_name || bank;
                                                        return (
                                                            <div
                                                                className={`bank_option ${selectedBank === bankName ? 'selected' : ''}`}
                                                                key={bank?.id || idx}
                                                                onClick={() => { setSelectedBank(bankName); }}
                                                            >
                                                                {bankName}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-primary btn-sm mt-2 w-100"
                                        onClick={() => setShowAddBankModal(true)}
                                    >
                                        + Add Bank
                                    </button>
                                </div>
                            </div>

                            <div className='col-md-8'>
                                <div className="v2_card v2_card_compliance" style={{ height: '100%' }}>
                                    <h3 className="v2_card_title">Compliance Overview</h3>
                                    <div className="gauge_wrap">
                                        <div className="gauge_chart" onClick={() => navigate('/domain-compliance')} style={{ cursor: 'pointer' }}>
                                            <div className="gauge_bg"></div>
                                            <div className="gauge_fill" style={{ '--percent': '87' }}></div>
                                            <div className="gauge_center">
                                                <span className="gauge_label">Compliance:</span>
                                                <span className="gauge_value">87%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Selector + Compliance Overview */}
                        <div className="dashboard_v2_cards dashboard_v2_top_row">
                            {/* Bank Selector */}


                            {/* Compliance Overview */}

                        </div>


                    </div>

                    {/* Risk Registry Section */}
                    {/* <div className='scroll_section' id="risk" ref={sectionsRef.risk}>
                    <Suspense fallback={<div></div>}>
                        <RiskRegistry />
                    </Suspense>
                </div> */}

                    {/* TPCRM Section */}
                    {/* <div className='scroll_section' id="tpcrm" ref={sectionsRef.tpcrm}>
                    <Suspense fallback={<div></div>}>
                        <ThirdPartyExternalExposure />
                    </Suspense>
                </div> */}

                    {/* GRC Section */}
                    {/* <div className='scroll_section' id="grc" ref={sectionsRef.grc}>
                    <Suspense fallback={<div></div>}>
                        <ComplianceReady />
                    </Suspense>
                </div> */}
                </div>
            </div>

            {/* Add Bank Modal */}
            <Modal
                show={showAddBankModal}
                onHide={() => { setShowAddBankModal(false); setAddBankErrors({}); }}
                centered
                className="cstm_modal white_border"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add Bank</Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-3'>
                    <form onSubmit={handleAddBank} noValidate>
                        <div className="form-group mb-3">
                            <label className="text-white mb-1">Bank Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter bank name"
                                value={addBankForm.bank_name}
                                onChange={(e) => { setAddBankForm(prev => ({ ...prev, bank_name: e.target.value })); setAddBankErrors(prev => ({ ...prev, bank_name: '' })); }}
                            />
                            {addBankErrors.bank_name && <small className="text-danger">{addBankErrors.bank_name}</small>}
                        </div>
                        <div className="form-group mb-3">
                            <label className="text-white mb-1">Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                className="form-control"
                                placeholder="Enter email"
                                value={addBankForm.email}
                                onChange={(e) => { setAddBankForm(prev => ({ ...prev, email: e.target.value })); setAddBankErrors(prev => ({ ...prev, email: '' })); }}
                            />
                            {addBankErrors.email && <small className="text-danger">{addBankErrors.email}</small>}
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={addBankLoading}
                        >
                            {addBankLoading ? <ButtonLoader /> : 'Add Bank'}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Toast */}
            {
                toast.show && (
                    <div className={`custom-toast ${toast.type}`}>
                        <span className="icon">
                            {toast.type === 'success' ? '✓' : '✕'}
                        </span>
                        <span className="message">{toast.message}</span>
                        <button
                            className="close-btn"
                            onClick={() => setToast({ show: false, message: '', type: 'success' })}
                        >
                            ✕
                        </button>
                    </div>
                )
            }
        </>
    );
}

export default DashboardV2;
